import React from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { C, DEFAULT_SOCIAL, media, useContent, useSite } from "./theme";
import { SectionHead, splitHeading } from "./Chrome";

const FILM = "#2A0A0F";
const sprockets = `repeating-linear-gradient(90deg, transparent 0 10px, ${C.cream}e6 10px 24px, transparent 24px 34px)`;

function Frame({ s, i, dup }) {
  return (
    <a href={s.href} target="_blank" rel="noreferrer" data-testid={dup ? undefined : `clean-social-${i}`} tabIndex={dup ? -1 : 0} className="group relative flex-shrink-0 flex flex-col px-3">
      <div className="relative w-[170px] md:w-[210px] aspect-[9/14] overflow-hidden rounded-[3px]" style={{ background: C.burgundy, boxShadow: `inset 0 0 0 1px ${C.wine}` }}>
        {s.img && <img src={media(s.img)} alt={`${s.client} ${s.series}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ filter: "sepia(0.35) contrast(1.05) saturate(0.85)" }} loading="lazy" />}
        <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `${C.wine}99` }}>
          <span className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: C.cream, color: C.burgundy }}><Play size={18} /></span>
        </span>
        <span className="absolute bottom-0 inset-x-0 p-2.5" style={{ background: `linear-gradient(transparent, ${C.wine}e6)` }}>
          <span className="block ed-serif text-base leading-tight" style={{ color: C.cream }}>{s.client}</span>
          <span className="block ed-mono text-[8px] uppercase tracking-[0.2em]" style={{ color: C.blushDeep }}>{s.series}{s.views ? ` · ${s.views} views` : ""}</span>
        </span>
      </div>
      <span className="ed-mono text-[8px] tracking-[0.3em] mt-1.5" style={{ color: C.goldLight }}>SA 400 ▸ {String(i + 1).padStart(2, "0")}A</span>
    </a>
  );
}

export function ReelsSection() {
  const { social } = useSite();
  const items = social.length ? social : DEFAULT_SOCIAL;
  const c = useContent("social");
  const [head, ital] = splitHeading(c.heading, "Personal brands for", "real people.");
  const reel = [...items, ...items, ...items];
  return (
    <section id="reels" data-testid="reels-section" className="relative min-h-screen flex flex-col justify-center py-24 md:py-28 overflow-hidden" style={{ background: C.cream }}>
      <div className="max-w-[1440px] w-full mx-auto px-6 md:px-12">
        <SectionHead num="04" eyebrow={c.eyebrow || "Reels · Social & personal brand"} heading={head} italic={ital} aside={c.blurb || "Content strategy, direction and captions for creators. Hover to pause the reel, click a frame to watch."} />
      </div>
      <div className="relative -rotate-[1.5deg] py-1" style={{ background: FILM, boxShadow: `0 30px 60px ${C.wine}40` }} data-testid="film-strip">
        <div className="h-5 mx-2" style={{ backgroundImage: sprockets, backgroundSize: "34px 12px", backgroundRepeat: "repeat-x", backgroundPosition: "0 center" }} />
        <div className="overflow-hidden group/film">
          <div className="flex w-max py-3 animate-[film_40s_linear_infinite] group-hover/film:[animation-play-state:paused]">
            {reel.map((s, k) => <Frame key={k} s={s} i={k % items.length} dup={k >= items.length} />)}
          </div>
        </div>
        <div className="h-5 mx-2" style={{ backgroundImage: sprockets, backgroundSize: "34px 12px", backgroundRepeat: "repeat-x", backgroundPosition: "0 center" }} />
        <motion.span className="absolute inset-0 pointer-events-none" animate={{ opacity: [0.06, 0.12, 0.05, 0.1, 0.06] }} transition={{ duration: 0.6, repeat: Infinity }} style={{ background: C.cream, mixBlendMode: "overlay" }} />
        <span className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='g'><feTurbulence baseFrequency='0.85' numOctaves='2'/></filter><rect width='160' height='160' filter='url(%23g)'/></svg>\")" }} />
      </div>
    </section>
  );
}
